import { useState } from "react";

const officeLocations = [
  { name: "New Delhi", lat: 28.6139, lng: 77.2090 },
  { name: "Morena", lat: 26.4977, lng: 77.9997 },
  { name: "Agra", lat: 27.1767, lng: 78.0081 },
  { name: "Ghaziabad", lat: 28.6692, lng: 77.4538 },
];

function IndiaMap({ projects, onSelectProject }) {
  const [hoveredPin, setHoveredPin] = useState(null);
  const [expandedPin, setExpandedPin] = useState(null);
  const [hoveredOffice, setHoveredOffice] = useState(null);

  // India geo bounds
  const minLat = 6;
  const maxLat = 38;
  const minLng = 68;
  const maxLng = 97;

  const latToPercentY = (lat) =>
    ((maxLat - lat) / (maxLat - minLat)) * 100 + 2.5;

  const lngToPercentX = (lng) =>
    ((lng - minLng) / (maxLng - minLng)) * 100 + 3;

  const getFirstImage = (project) =>
    project.images && project.images.length > 0 ? project.images[0] : null;

  // Group projects by lat/lng key
  const pinMap = {};
  projects.forEach((project) => {
    if (!project.lat || !project.lng) return;
    const key = `${project.lat}_${project.lng}`;
    if (!pinMap[key]) {
      pinMap[key] = { lat: project.lat, lng: project.lng, projects: [] };
    }
    pinMap[key].projects.push(project);
  });

  const pins = Object.entries(pinMap);

  return (
    <div className="w-full flex justify-center py-6">
      <div className="relative w-full max-w-3xl select-none">

        {/* Overlay to close expanded pin on outside click */}
        {expandedPin && (
          <div
            className="fixed inset-0 z-[9980]"
            onClick={() => { setExpandedPin(null); setHoveredPin(null); }}
          />
        )}

        {/* India Map Image */}
        <img
          src="/India-map2.png"
          alt="India Map"
          className="w-full h-auto opacity-20 pointer-events-none"
          draggable={false}
        />

        {/* Legend */}
        <div className="absolute -top-7 right-0 sm:top-2 sm:right-2 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-2 shadow text-xs text-gray-600 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500 inline-block shrink-0 ring-2 ring-orange-300" />
            Click a pin to view project
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-500 inline-block shrink-0 ring-2 ring-blue-300" />
            Office location
          </div>
        </div>

        {/* Office Pins */}
        {officeLocations.map((office) => {
          const top = latToPercentY(office.lat);
          const left = lngToPercentX(office.lng);
          const isHovered = hoveredOffice === office.name;
          const flipLeft = left > 65;
          const flipUp = top > 65;
          return (
            <div
              key={office.name}
              className="absolute"
              style={{
                top: `${top}%`,
                left: `${left}%`,
                transform: "translate(-50%, -50%)",
                zIndex: isHovered ? 9990 : 20,
              }}
              onMouseEnter={() => setHoveredOffice(office.name)}
              onMouseLeave={() => setHoveredOffice(null)}
            >
              {isHovered && (
                <span className="absolute inset-0 rounded-full animate-ping bg-blue-400 opacity-60 w-5 h-5" />
              )}
              <div
                className={`relative flex items-center justify-center rounded-full border-2 border-white shadow-md w-4 h-4 bg-blue-500 transition-transform duration-200 ${
                  isHovered ? "scale-150" : "hover:scale-125"
                }`}
              />
              {isHovered && (
                <div
                  className={`absolute ${
                    flipUp ? "bottom-6" : "top-[-8px]"
                  } ${
                    flipLeft ? "right-6" : "left-6"
                  } bg-white rounded-lg shadow-xl border border-blue-100 px-3 py-1.5 whitespace-nowrap`}
                  style={{ zIndex: 9999 }}
                >
                  <p className="text-xs font-semibold text-blue-600">{office.name}</p>
                  <p className="text-[10px] text-gray-400">ATPPL Office</p>
                </div>
              )}
            </div>
          );
        })}

        {/* Project Pins */}
        {pins.map(([key, pin]) => {
          const top = latToPercentY(pin.lat);
          const left = lngToPercentX(pin.lng);
          const isHovered = hoveredPin === key;
          const isExpanded = expandedPin === key;
          const isGroup = pin.projects.length > 1;
          const flipLeft = left > 65;
          const flipUp = top > 65;

          return (
            <div
              key={key}
              className="absolute"
              style={{
                top: `${top}%`,
                left: `${left}%`,
                transform: "translate(-50%, -50%)",
                zIndex: isExpanded || isHovered ? 9990 : 10,
              }}
              onMouseEnter={() => setHoveredPin(key)}
              onMouseLeave={() => { if (!isExpanded) setHoveredPin(null); }}
            >
              {/* Pulsing ring on hover */}
              {isHovered && (
                <span className="absolute inset-0 rounded-full animate-ping bg-orange-400 opacity-60 w-5 h-5" />
              )}

              {/* Pin dot */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGroup) {
                    setExpandedPin(isExpanded ? null : key);
                  } else {
                    onSelectProject && onSelectProject(pin.projects[0]);
                  }
                }}
                className={`relative flex items-center justify-center rounded-full border-2 border-white shadow-md cursor-pointer transition-transform duration-200 ${
                  isGroup ? "w-6 h-6 bg-orange-600" : "w-4 h-4 bg-orange-500"
                } ${isHovered ? "scale-150" : "hover:scale-125"}`}
              >
                {isGroup && (
                  <span className="text-white text-[9px] font-bold leading-none">
                    {pin.projects.length}
                  </span>
                )}
              </div>

              {/* Group expanded list popup */}
              {isExpanded && isGroup && (
                <div
                  className={`absolute ${flipUp ? "bottom-6" : "top-[-8px]"} ${
                    flipLeft ? "right-6" : "left-6"
                  } bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden`}
                  style={{ zIndex: 9999, width: "220px" }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => setHoveredPin(key)}
                  onMouseLeave={() => setHoveredPin(null)}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-3 py-2 bg-orange-50 border-b border-orange-100">
                    <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide">
                      {pin.projects.find(p => p.regionLabel)?.regionLabel || pin.projects[0].location}
                    </p>
                    <button
                      onClick={() => setExpandedPin(null)}
                      className="text-gray-400 hover:text-gray-600 text-sm leading-none"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Project list */}
                  <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                    {pin.projects.map((proj) => (
                      <li
                        key={proj.id}
                        onClick={() => {
                          setExpandedPin(null);
                          onSelectProject && onSelectProject(proj);
                        }}
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-orange-50 transition-colors"
                      >
                        {getFirstImage(proj) ? (
                          <img
                            src={getFirstImage(proj)}
                            alt={proj.title}
                            loading="lazy"
                            className="w-10 h-10 rounded-md object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-md bg-gray-100 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-800 leading-snug truncate">
                            {proj.title}
                          </p>
                          {proj.period && (
                            <p className="text-[10px] text-orange-500 mt-0.5">
                              {proj.period}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default IndiaMap;
